"use client"

import { FC, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { Button } from '@/components/ui'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import type { ColumnJSON } from '../../../../src/types/table-json'

export interface MultiColumnSelectorProps {
  /**
   * The columns metadata from TableDataJSON
   */
  columns?: ColumnJSON[]
  
  /**
   * The currently selected column names
   */
  value?: string[]
  
  /**
   * Label to display above the selector
   */
  label?: string
  
  /**
   * Placeholder text when no columns are selected
   */
  placeholder?: string
  
  /**
   * Called when columns are selected/deselected
   */
  onChange?: (columns: string[]) => void
  
  /**
   * Whether to only show numeric columns
   */
  numericOnly?: boolean

  /**
   * Whether the selector is required
   */
  required?: boolean

  /**
   * Minimum number of selections required
   */
  minSelections?: number

  /**
   * Maximum number of selections allowed
   */
  maxSelections?: number
  
  /**
   * Whether the selector is disabled
   */
  disabled?: boolean

  /**
   * Additional CSS classes to apply to the root element
   */
  className?: string
}

export const MultiColumnSelector: FC<MultiColumnSelectorProps> = ({
  columns = [],
  value = [],
  label,
  placeholder = 'Select columns...',
  onChange,
  numericOnly,
  required,
  minSelections = 0,
  maxSelections,
  disabled,
  className = ''
}) => {
  const [open, setOpen] = useState(false)

  const numericColumns = numericOnly
    ? columns.filter(col => col.values.every(v => !isNaN(Number(v))))
    : columns

  const handleSelect = (currentValue: string) => {
    if (!onChange || disabled) return

    const isSelected = value.includes(currentValue)
    let newValue: string[]

    if (isSelected) {
      if (value.length <= minSelections) return
      newValue = value.filter(v => v !== currentValue)
    } else {
      if (maxSelections !== undefined && value.length >= maxSelections) return
      newValue = [...value, currentValue]
    }

    onChange(newValue)
  }

  const selectedColumns = numericColumns.filter(col => value.includes(col.id))
  const selectedText = selectedColumns.length > 0
    ? selectedColumns.length === 1
      ? selectedColumns[0]?.name ?? placeholder
      : `${selectedColumns[0]?.name} +${selectedColumns.length - 1}`
    : placeholder

  return (
    <div className={cn("space-y-2", className)} data-testid="multi-column-selector">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium leading-none">
            {label}
          </label>
          {required && (
            <span className="text-red-500 text-xs leading-none">*</span>
          )}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            <span className="truncate flex-1 text-left">{selectedText}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--trigger-width] p-0" style={{ "--trigger-width": "var(--radix-popover-trigger-width)" } as any}>
          <Command>
            <CommandInput placeholder="Search columns..." />
            <CommandList>
              <CommandEmpty>No columns found.</CommandEmpty>
              <CommandGroup>
                {numericColumns.map((column) => (
                  <CommandItem
                    key={column.id}
                    value={column.name}
                    onSelect={() => handleSelect(column.id)}
                    role="option"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value.includes(column.id) ? "opacity-100" : "opacity-0"
                      )}
                      data-testid="check-icon"
                    />
                    {column.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedColumns.length > 0 && (
        <div className="text-xs text-muted-foreground mt-1">
          Selected: {selectedColumns.map(col => col.name).join(', ')}
        </div>
      )}
    </div>
  )
} 